/**
 * Historical scenes feature — the top-right clock button + centered slider that
 * lets the user browse alternate-era versions of a main scene (e.g. the Kaaba
 * across centuries). See docs/architecture/scene-registry.md.
 *
 * Era scenes are children of the main scene on the stack (`[kaaba, era]`),
 * modelled like inside-scenes so a future "Back to {parent}" button drops in
 * cleanly. This module owns:
 *   - `$historyMode` — closed / slider state (reset on any navigation).
 *   - `resolveHistoricalFamily` / `sceneHasHistory` — pure id → slides lookup.
 *   - `navigateToHistoricalScene` — the single navigation entry point used by
 *     the slider's "Explore" CTA (push child / replace sibling / goBack to main).
 *
 * Kept separate from `eraScenes/registry.ts` to avoid an import cycle with
 * `splatRegistry` (this module imports `getSplatSceneConfig`).
 */
import { createEvent, createStore, sample } from 'effector';
import type { HistoricalSceneSlide } from '../scene/sceneConfig';
import { IS_BALADY } from '../config/appConfig';
import { getSplatSceneConfig } from '../scene/splatRegistry';
import { ERA_SCENE_PARENT, isStubEraScene } from '../scene/eraScenes/registry';
import { SCENE_POI_ID } from '../scene/sceneNavigationMap';
import {
  $activeScene,
  $sceneStack,
  enterScene,
  replaceScene,
  goBack,
  rebuildStack,
  resetNavigation,
  openPoiArtifact,
} from './sceneNavigation';
import { sceneExited } from '../analytics/sceneTracker';

// --- Mode: closed → slider (era cards) ---
export type HistoryMode = 'closed' | 'slider';

/** Open the era-card slider. */
export const openHistorySlider = createEvent();
/** Dismiss the whole feature (back to the scene). */
export const closeHistory = createEvent();
/** Clock-button tap — routed below to slider / close. */
export const clockButtonTapped = createEvent();

/**
 * Which historical-scenes surface is showing. The clock toggles the `slider`.
 * Reset to `closed` on ANY scene navigation (incl. the era nav this module
 * triggers) + on PoiArtifact open, so nothing lingers across a scene change.
 */
export const $historyMode = createStore<HistoryMode>('closed')
  .on(openHistorySlider, () => 'slider')
  .on(closeHistory, () => 'closed')
  .reset(resetNavigation, openPoiArtifact, enterScene, replaceScene, goBack);

// Clock-tap routing: open → close; closed → slider. The two filters are
// mutually exclusive and read the same pre-update snapshot, so exactly one
// fires per tap.
sample({
  clock: clockButtonTapped,
  source: $historyMode,
  filter: (mode) => mode !== 'closed',
  target: closeHistory,
});
sample({
  clock: clockButtonTapped,
  source: $historyMode,
  filter: (mode) => mode === 'closed',
  target: openHistorySlider,
});

// --- Family resolution (pure) ---
export type HistoricalFamily = {
  /** URL-id of the main scene this family belongs to (e.g. `'kaaba'`). */
  mainSceneId: string;
  /** Ordered slides (incl. the "today" card = the main scene itself). */
  slides: HistoricalSceneSlide[];
};

/**
 * Resolve the historical family for a scene id — works whether the id is the
 * main scene (`'kaaba'`) or one of its eras (`'kaaba-1890s'`). Returns `null`
 * when the scene has no historical scenes declared.
 *
 * Single chokepoint of the whole feature: returning `null` under the Balady
 * build flag hides the clock button, the slider and the menu's «Historical
 * Scenes» tab at once (deep links like `/kaaba/kaaba-1890s` still render the
 * era scene — they bypass this lookup).
 */
export const resolveHistoricalFamily = (
  sceneId: string | null | undefined,
): HistoricalFamily | null => {
  if (IS_BALADY) return null;
  if (!sceneId) return null;
  const mainSceneId = ERA_SCENE_PARENT[sceneId] ?? sceneId;
  const slides = getSplatSceneConfig(mainSceneId)?.historicalScenes;
  if (!slides || slides.length === 0) return null;
  // Guard: the id must actually belong to this family (be the main scene or one
  // of its listed slides) — protects against an arbitrary id whose `parent`
  // happens to map nowhere meaningful.
  const belongs =
    sceneId === mainSceneId || slides.some((slide) => slide.sceneId === sceneId);
  if (!belongs) return null;
  return { mainSceneId, slides };
};

/** Does this scene expose the historical-scenes slider (= clock button)? */
export const sceneHasHistory = (sceneId: string | null | undefined): boolean =>
  resolveHistoricalFamily(sceneId) !== null;

// --- Navigation ---
/**
 * Navigate to a historical scene from the slider's "Explore" CTA. Parent-child
 * model:
 *   - target === current → no-op (the current slide's Explore is disabled anyway).
 *   - target === main ("today") → `goBack()` if currently in an era stacked
 *     under that main (fast pop); otherwise enter it fresh.
 *   - current === main, target = era → `enterScene` (push child).
 *   - current = era, target = sibling era → `replaceScene` (replace top, keep parent).
 *   - current is OUTSIDE the target's family (an inside-scene, another
 *     location) → rebuild the stack as `[main, era]`. Without this the whole
 *     call used to bail out on the `family` guard below, which is how the
 *     next-scene chip pointing at an era from `kaaba-inside` did nothing.
 *
 * `$historyMode` is reset by the navigation events themselves (see store
 * above), so the overlay closes automatically.
 *
 * Stub eras (no scene built yet — `isStubEraScene`) and the whole Balady build
 * (`IS_BALADY`) are refused here, the single chokepoint every UI path goes
 * through: the slider and menu cards render stubs locked with a "Coming soon"
 * badge and no CTA, so this only backstops a future caller. Deep links
 * (`/kaaba/kaaba-1890s`) deliberately still work — handy for QA/tuning of the
 * placeholder splat.
 */
export const navigateToHistoricalScene = (targetSceneId: string): void => {
  if (isStubEraScene(targetSceneId) || IS_BALADY) return;
  const activeId = $activeScene.getState()?.sceneId ?? null;
  if (!activeId || targetSceneId === activeId) return;

  const poiFilterId = SCENE_POI_ID[targetSceneId] ?? targetSceneId;
  const family = resolveHistoricalFamily(activeId);
  // A family the target doesn't belong to is no stack to push or replace on:
  // that would build a parent-mismatched stack and a non-reloadable URL — it
  // enters like any out-of-family target.
  const targetInFamily =
    family !== null &&
    (targetSceneId === family.mainSceneId ||
      family.slides.some((slide) => slide.sceneId === targetSceneId));

  if (!family || !targetInFamily) {
    // Entry from outside the family (inside-scene, another location, the
    // other engine's family): there is no stack to push onto — rebuild it as
    // `[main, era]`.
    const parentId = ERA_SCENE_PARENT[targetSceneId];
    if (!parentId) return;
    sceneExited(activeId);
    resetNavigation();
    rebuildStack([
      { sceneId: parentId, poiFilterId: SCENE_POI_ID[parentId] ?? parentId },
      { sceneId: targetSceneId, poiFilterId },
    ]);
    return;
  }

  const { mainSceneId } = family;

  if (targetSceneId === mainSceneId) {
    // Returning to the live "today" scene.
    const stack = $sceneStack.getState();
    const parent = stack.length > 1 ? stack[stack.length - 2] : null;
    if (parent && parent.sceneId === mainSceneId) {
      goBack(); // pop era → main (parent preserved on the stack)
    } else {
      enterScene({ sceneId: targetSceneId, poiFilterId });
    }
    return;
  }

  if (activeId === mainSceneId) {
    // main → era: push the era as a child of the main scene.
    enterScene({ sceneId: targetSceneId, poiFilterId });
  } else {
    // era → sibling era: replace the stack top, keep the parent for goBack.
    replaceScene({ sceneId: targetSceneId, poiFilterId });
  }
};
